<template>
  <div>
    <v-expand-transition>
      <Async :loading="loading">
        <v-card-text>
          <div class="flex-center">
            <label
              @dragover.prevent
              @dragenter="isGrabing = true"
              @drop.prevent="handleDrop"
              @dragend="isGrabing = false"
              @dragleave="isGrabing = false"
              @click="(e) => !!previewURL && e.preventDefault()"
              :class="!!previewURL ? 'w60p' : 'w100p'"
              class="cursor-pointer"
            >
              <v-img
                :src="previewURL"
                :aspect-ratio="!!previewURL ? null : 24 / 9"
                contain
              >
                <div
                  class="file_area fill-all flex-center"
                  :class="{ isGrabing, isError: !!fileError }"
                  v-if="!previewURL"
                >
                  <slot name="placeholder">
                    <div class="text-center">
                      <div class="text-h6 mb-1">
                        <v-icon>mdi-plus</v-icon>
                        {{ $t('COMMON.WORD.UPLOAD_FILE') }}
                      </div>
                      <p>
                        {{ $t('ARTWORK.EDIT.COPYWRITING.SUPPORT') }}
                        {{ acceptExt.replace(/\./g, '').replace(/,/g, ', ') }}
                        {{ $t('ARTWORK.EDIT.WORD.FORMAT_MAXIMUM') }}
                        {{ fileLimit }}MB
                      </p>
                    </div>
                  </slot>
                </div>
              </v-img>
              <input
                ref="fileInput"
                @change="handleFileChange"
                type="file"
                hidden
                :accept="acceptExt"
              />
            </label>
          </div>
          <v-card v-if="!!fileError" flat color="transparent" min-height="24">
            <div class="flex-center">
              <span class="error--text">
                {{ fileError }}
              </span>
            </div>
          </v-card>
        </v-card-text>

        <v-card-actions class="flex-center py-0">
          <v-btn v-show="!!previewURL" text @click="previewURL = null">
            <v-icon>mdi-reload</v-icon>
            {{ $t('ARTWORK.EDIT.WORD.UPLOAD_AGAIN') }}
          </v-btn>
        </v-card-actions>
      </Async>
    </v-expand-transition>
  </div>
</template>

<script>
export default {
  props: {
    fileLimit: {
      type: Number,
      default: 5
    },
    value: {
      type: [Object, String, File, Blob],
      default: null
    },
    img: {
      type: String,
      default: ''
    },
    rules: {
      type: Array, // Array<Function>
      default: () => [],
      validator(v) {
        if (v.length) {
          return !v.some((_v) => typeof _v !== 'function');
        } else {
          return true;
        }
      }
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      acceptExt: '.jpg,.jpeg,.png',
      holdingFile: null, // only for: image, sound, 3dmodel
      uploadMethod: '', // upload, ipfs
      isGrabing: false,
      fileError: null,
      isUploadingPreview: false,
      previewedImageName: '',

      // image
      previewURL: this.img
    };
  },

  watch: {
    img(v) {
      this.previewURL = v;
    },
    previewURL(v) {
      this.fileError = null;
      if (!v) {
        this.$refs.fileInput && (this.$refs.fileInput.value = null);
        this.holdingFile = null;
      }
      this.$emit('update-img', v);
    },
    holdingFile(v) {
      !v && (this.previewURL = null);
      this.$emit('input', v);
    }
  },

  methods: {
    $validate() {
      for (let i = 0; i < this.rules.length; i++) {
        const rule = this.rules[i];

        if (
          rule &&
          typeof rule === 'function' &&
          ((this.previewURL && !this.previewURL.includes('http')) ||
            !this.previewURL)
        ) {
          const result = rule(this.value);
          this.fileError = result === true ? null : result;
        } else {
          this.fileError = null;
        }
      }

      return this.fileError === null;
    },
    handleDrop(e) {
      this.isGrabing = false;
      if (e.dataTransfer.files.length) {
        const file = e.dataTransfer.files[0];
        const exts = this.acceptExt.replaceAll('.', '').split(',');

        if (exts.includes(file.name.split('.').pop())) {
          this.handlePreview(file);
        } else {
          this.$toast.error('ARTWORK.EDIT.TOAST.ALERT.FILE_FORMAT_NOT_MATCH');
        }
      }
    },
    handleFileChange(e) {
      if (e.target.files.length) {
        const file = e.target.files[0];
        this.handlePreview(file);
      }
    },

    async handlePreview(file) {
      if (file.size > 1048576 * this.fileLimit) {
        this.$toast.error(
          `${this.$t('ARTWORK.EDIT.COPYWRITING.FILE_SIZE_NOT_EXCEED')} ${
            this.fileLimit
          }MB`
        );
        return;
      }
      this.holdingFile = file;
      if (file) {
        const url = URL.createObjectURL(file);

        const Result = await this.$shrinkImg(url, {
          minWidth: 256,
          minHeight: 256,
          targetSize: 1048576 * this.fileLimit,
          maxWidth: 1024,
          maxHeight: 1024,
          type: file.type
        });

        this.holdingFile = Result.tests ? Result.file : file;
        this.previewURL = Result.imgURL;
      }
    },

    checkYTURL(text) {
      const urlReg =
        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/;
      const isUrl = urlReg.test(text);
      const hasYoutube = /youtube.com|youtu.be\//.test(text);

      return (
        (isUrl && hasYoutube) ||
        this.$t('ARTWORK.EDIT.INPUT.ALERT.INVALID_URL_FORMAT')
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.file_area {
  * {
    user-select: none;
  }
  position: relative;
  border: 1px dashed;
  border-color: $neutral300;
  border-radius: 6px;
  background-color: $neutral700;

  &.isError {
    border-color: $error;
  }

  &.isGrabing {
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      width: calc(100% - 8px);
      height: calc(100% - 8px);
      background-color: rgba($neutral100, $alpha: 0.6);
      border-radius: 6px;
      border: 2px solid $primary;
    }
  }
}
</style>